import { ERROR_FREE_MESSAGES } from '@/constants/orders';
import { SIGN_ERROR_CODES } from '@/constants/sign';
import { DocumentService } from '@/services/DocumentService';
import { SigningErrorResponse } from '@/types/sign';
import { UseMutateFunction } from '@tanstack/react-query';
import { toSentenceCase } from '@treyd-io/core/utils/string';
import { useToastNotification } from '@treyd-io/treyd-ui/hooks/useToastNotification';
import { AxiosError, AxiosResponse } from 'axios';
import { includes } from 'lodash';
import { useTranslation } from 'react-i18next';

export const usePhoneOtp = <TPayload, TError extends SigningErrorResponse>({
  signFn,
  cancelFn,
}: {
  signFn: UseMutateFunction<
    AxiosResponse,
    AxiosError<TError>,
    TPayload,
    unknown
  >;
  cancelFn: UseMutateFunction<
    AxiosResponse,
    AxiosError<unknown>,
    // biome-ignore lint/suspicious/noExplicitAny:
    any,
    unknown
  >;
}) => {
  const notification = useToastNotification();
  const { t } = useTranslation();
  const documentService = new DocumentService({ t });

  const onError = (error: AxiosError<TError>) => {
    const errorCode = error?.response?.data?.error_code;
    if (
      !includes(
        ERROR_FREE_MESSAGES,
        error.response?.data.error?.toLocaleLowerCase()
      ) ||
      errorCode === SIGN_ERROR_CODES.PHONE_NUMBER_ERROR
    ) {
      notification({
        title: errorCode
          ? documentService.signingErrorMessagesMapper[errorCode]
          : toSentenceCase(error.response?.data.error || ''),
        type: 'error',
      });
    } else {
      notification({
        title: t('otp_sent_successfully'),
        type: 'success',
      });
    }
  };

  const onCancel = async (payload: TPayload) => {
    cancelFn(undefined, {
      onSettled: () => {
        signFn(payload, {
          onError,
        });
      },
    });
  };

  const onSign = (payload: TPayload) => {
    signFn(payload, {
      onError: (error) => {
        if (error.response?.data?.otp_valid === false) {
          notification({
            title: t('invalid_otp'),
            type: 'error',
          });
        }
      },
    });
  };

  return {
    onCancel,
    onSign,
  };
};
